import BackgroundImageHelper, {
  Container as BackgroundImageHelperContainer,
} from "@components/BackgroundImageHelper"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14Styles,
  h2Styles,
  h3Styles,
  h4Styles,
  paragraph18Styles,
  paragraph23Styles,
  paragraphStyles,
} from "@constants/typography"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  display: flex;

  @media ${breakpoints.laptop} {
    height: 453px;
  }
`

const Header = styled.div`
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 200px;
  padding: 0 20px;
  text-align: center;

  @media ${breakpoints.laptop} {
    height: 453px;
    padding: 0 calc(2 / 12 * 100vw);
  }
`

const HeaderHeroText = styled.h1`
  ${h4Styles}
  color: ${colors.offWhite};

  @media ${breakpoints.laptop} {
    ${h2Styles}
    color: ${colors.offWhite};
  }
`

const HeaderText = styled.h1`
  ${h4Styles}
  color: ${colors.offWhite};
  margin-bottom: 8px;

  @media ${breakpoints.laptop} {
    ${h2Styles}
    color: ${colors.offWhite};
    margin-bottom: 16px;
  }
`

const SubheaderText = styled.div`
  ${body14Styles}
  color: ${colors.offWhite};

  @media ${breakpoints.laptop} {
    ${paragraph23Styles}
    color: ${colors.offWhite};
  }
`

const Paragraph = styled.div`
  z-index: 1;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  display: flex;
  width: 100%;
  padding: 40px 20px;

  @media ${breakpoints.laptop} {
    position: absolute;
    width: 50%;
    height: 100%;
    padding: 0;
    padding-left: calc(0.5 / 12 * 100vw);
  }
`

const ParagraphHeader = styled.h1`
  ${h4Styles}
  color: ${colors.black};
  margin-bottom: 24px;

  @media ${breakpoints.laptop} {
    ${h3Styles}
    margin-bottom: 24px;
    color: ${colors.offWhite};
  }
`

const ParagraphBody = styled.div`
  ${paragraphStyles}
  color: ${colors.black};

  @media ${breakpoints.laptop} {
    ${paragraph18Styles}
    color: ${colors.offWhite};
  }
`

const FullWidthHeaderContainer = styled.div`
  position: relative;

  ${BackgroundImageHelperContainer} {
    flex-shrink: 0;
    align-self: stretch;
    justify-self: stretch;
    display: flex;
    width: 100vw;
    height: 200px;

    @media ${breakpoints.laptop} {
      height: 453px;
    }
  }
`

const FullWidthParagraphContainer = styled.div`
  position: relative;
  flex-direction: row;

  @media ${breakpoints.laptop} {
    flex-direction: column;
  }

  ${BackgroundImageHelperContainer} {
    flex-shrink: 0;
    align-self: stretch;
    justify-self: stretch;
    display: flex;
    width: 100vw;
    height: 200px;

    @media ${breakpoints.laptop} {
      height: 453px;
    }
  }
`

const SplitContainer = styled.div`
  position: relative;
  flex-direction: row;

  @media ${breakpoints.laptop} {
    flex-direction: column;
  }

  ${Paragraph} {
    @media ${breakpoints.laptop} {
      position: relative;
      height: 100%;
      padding: 0;
      padding: 0 calc(0.5 / 12 * 100vw);
    }
  }

  ${ParagraphHeader} {
    color: ${colors.black};
  }

  ${ParagraphBody} {
    color: ${colors.black};
  }
`

const SplitImageContainer = styled.div`
  ${BackgroundImageHelperContainer} {
    flex-shrink: 0;
    align-self: stretch;
    justify-self: stretch;
    display: flex;
    width: 100vw;
    height: 200px;

    @media ${breakpoints.laptop} {
      width: calc(7.5 / 12 * 100vw);
      height: 453px;
    }
  }
`

export enum BannerTypes {
  HEADER = "header",
  IMAGE = "image",
  PARAGRAPH = "paragraph",
}

interface BannerProps {
  bannerHeader?: string
  bannerImage?: string
  bannerSubheader?: string
  collectionBannerType?: string
  mobileBannerImage?: string
  paragraphAsymmetricalSplit?: boolean
  paragraphBody?: string
  paragraphHeader?: string
}

const Banner: React.FC<BannerProps> = ({
  bannerHeader,
  bannerImage,
  bannerSubheader,
  collectionBannerType,
  mobileBannerImage,
  paragraphAsymmetricalSplit,
  paragraphBody,
  paragraphHeader,
}) => {
  if (!collectionBannerType || !bannerImage || !mobileBannerImage) {
    return null
  } else if (collectionBannerType === BannerTypes.HEADER && !bannerHeader) {
    return null
  } else if (
    collectionBannerType === BannerTypes.PARAGRAPH &&
    (!paragraphHeader || !paragraphBody)
  ) {
    return null
  }

  return (
    <Container>
      {collectionBannerType === BannerTypes.IMAGE && (
        <FullWidthHeaderContainer>
          <MobileAndTabletOnly>
            <BackgroundImageHelper src={mobileBannerImage!} />
          </MobileAndTabletOnly>
          <DesktopOnly>
            <BackgroundImageHelper src={bannerImage} />
          </DesktopOnly>
        </FullWidthHeaderContainer>
      )}
      {collectionBannerType === BannerTypes.HEADER && (
        <FullWidthHeaderContainer>
          <MobileAndTabletOnly>
            <BackgroundImageHelper src={mobileBannerImage!} />
          </MobileAndTabletOnly>
          <DesktopOnly>
            <BackgroundImageHelper src={bannerImage} />
          </DesktopOnly>
          <Header>
            {bannerSubheader ? (
              <>
                <HeaderText>{bannerHeader}</HeaderText>
                <SubheaderText>{bannerSubheader}</SubheaderText>
              </>
            ) : (
              <HeaderHeroText>{bannerHeader}</HeaderHeroText>
            )}
          </Header>
        </FullWidthHeaderContainer>
      )}
      {collectionBannerType === BannerTypes.PARAGRAPH &&
        (paragraphAsymmetricalSplit ? (
          <SplitContainer>
            <Paragraph>
              <ParagraphHeader>{paragraphHeader}</ParagraphHeader>
              <ParagraphBody>{paragraphBody}</ParagraphBody>
            </Paragraph>
            <SplitImageContainer>
              <MobileAndTabletOnly>
                <BackgroundImageHelper src={mobileBannerImage!} />
              </MobileAndTabletOnly>
              <DesktopOnly>
                <BackgroundImageHelper src={bannerImage} />
              </DesktopOnly>
            </SplitImageContainer>
          </SplitContainer>
        ) : (
          <FullWidthParagraphContainer>
            <Paragraph>
              <ParagraphHeader>{paragraphHeader}</ParagraphHeader>
              <ParagraphBody>{paragraphBody}</ParagraphBody>
            </Paragraph>
            <MobileAndTabletOnly>
              <BackgroundImageHelper src={mobileBannerImage!} />
            </MobileAndTabletOnly>
            <DesktopOnly>
              <BackgroundImageHelper src={bannerImage} />
            </DesktopOnly>
          </FullWidthParagraphContainer>
        ))}
    </Container>
  )
}

export default Banner
